<script setup>
import { computed } from 'vue';
import { Dropdown as UiDropdown, DropdownItem as UiDropdownItem } from '../ui/index.js';
import UiButton from '@/components/ui/Button.vue';
import DotSeparator from '@/components/ui/DotSeparator.vue';
import ScheduleInfoTooltip from '@/components/courses/ScheduleInfoTooltip.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  offerings: {
    type: Array,
    default: () => []
  },
  trigger: {
    type: String,
    default: 'hover'
  },
  hoverDelay: {
    type: Number,
    default: 200
  },
  buttonVariant: {
    type: String,
    default: 'maroon'
  },
  buttonText: {
    type: String,
    default: 'Add to schedule'
  },
  buttonBlock: {
    type: Boolean,
    default: false
  },
  buttonStyle: {
    type: String,
    default: ''
  },
  tooltipText: {
    type: String,
    default: 'Choose your learning schedule preference'
  }
});

const emit = defineEmits(['update:modelValue', 'select', 'click']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const normalizeOnDemandLabel = (text) => {
  if (!text) return text;
  return text.replace(/\bOn Demand\b/gi, 'On-demand');
};

const scheduleOptions = computed(() => {
  const options = props.offerings.map((offering) => ({
    id: offering.offeringId,
    label: normalizeOnDemandLabel(offering.sessionName),
    sessionDates: offering.sessionDates || null,
    length: offering.length && /^\d+\s+weeks?$/i.test(offering.length) ? offering.length : null,
    offering,
    // Use backend-provided disabled state
    disabled: offering.disabled === true
  }));

  // Sort so OnDemand appears last
  return options.sort((a, b) => {
    if (a.offering.type === 'OnDemand' && b.offering.type !== 'OnDemand') return 1;
    if (a.offering.type !== 'OnDemand' && b.offering.type === 'OnDemand') return -1;
    return 0;
  });
});

const handleButtonClick = (event) => {
  // Don't stop propagation here - let it reach the Dropdown component
  // The Dropdown component will handle stopping propagation to parent elements
  emit('click', event);
};

const handleScheduleSelect = (option) => {
  emit('select', option);
};
</script>

<template>
  <UiDropdown v-model="isOpen" :trigger="trigger" :hover-delay="hoverDelay" :block="buttonBlock" placement="bottom-end">
    <template #trigger="{ isOpen: dropdownOpen }">
      <UiButton
        :variant="buttonVariant"
        :block="buttonBlock"
        :style="buttonStyle"
        :dropdown="true"
        :dropdown-open="dropdownOpen"
        @click="handleButtonClick"
      >
        {{ buttonText }}
      </UiButton>
    </template>

    <template #default="{ close }">
      <div style="min-width: 400px">
        <div style="display: flex; justify-content: space-between; align-items: center" class="pb-3">
          <h4 style="font-size: 16px; font-weight: 600; color: #333; margin: 0; text-align: left">
            Session-based or on-demand
          </h4>
          <ScheduleInfoTooltip />
        </div>

        <UiDropdownItem
          v-for="option in scheduleOptions"
          :key="option.id"
          :disabled="option.disabled"
          @click="
            !option.disabled && handleScheduleSelect(option);
            !option.disabled && close();
          "
        >
          <div class="d-flex align-center" style="width: 100%; line-height: 24px; gap: 8px">
            <span
              :style="{
                'font-weight': '400',
                color: option.disabled ? '#BDBDBD' : '#191919',
                'font-size': '16px',
                'line-height': '24px'
              }"
            >
              {{ option.label }}
            </span>
            <template v-if="option.sessionDates">
              <DotSeparator />
              <span
                :style="{
                  'font-weight': '400',
                  color: option.disabled ? '#BDBDBD' : '#484848',
                  'font-size': '16px',
                  'line-height': '24px'
                }"
              >
                {{ option.sessionDates }}
              </span>
            </template>
            <template v-if="option.length">
              <DotSeparator />
              <span
                :style="{
                  'font-weight': '400',
                  color: option.disabled ? '#BDBDBD' : '#484848',
                  'font-size': '16px',
                  'line-height': '24px'
                }"
              >
                {{ option.length }}
              </span>
            </template>
            <v-icon v-if="option.disabled" style="font-size: 20px"> mdi-check-bold </v-icon>
          </div>
        </UiDropdownItem>
      </div>
    </template>
  </UiDropdown>
</template>
